import React, { useState, useEffect } from 'react';
import { COLORS } from '../App';
import TemplateSelector from './TemplateSelector';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';
function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then(r => r.json());
}

async function authDownload(url, filename) {
  const token = localStorage.getItem('cmbs_token');
  const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
  if (!res.ok) { alert('Download failed'); return; }
  const blob = await res.blob();
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename || 'download';
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(a.href);
}

export default function ModelPopulator({ deal, onUpdate }) {
  const [models, setModels] = useState([]);
  const [progress, setProgress] = useState(null);
  const [populating, setPopulating] = useState(false);

  useEffect(() => { loadData(); }, [deal.id]);

  const loadData = async () => {
    const [modelsRes, progressRes] = await Promise.all([
      apiFetch(`/populate/${deal.id}`),
      apiFetch(`/review/progress/${deal.id}`),
    ]);
    if (modelsRes.success) setModels(modelsRes.data);
    if (progressRes.success) setProgress(progressRes.data);
  };

  const handlePopulate = async () => {
    setPopulating(true);
    const res = await apiFetch(`/populate/${deal.id}`, { method: 'POST' });
    if (res.success) {
      await loadData();
      onUpdate();
    } else {
      alert(res.error || 'Failed to populate model');
    }
    setPopulating(false);
  };

  const card = { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 24, marginBottom: 20 };

  const canPopulate = progress && parseInt(progress.reviewed) > 0;
  const allReviewed = progress && parseInt(progress.pending) === 0 && parseInt(progress.total) > 0;

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 20 }}>Populate UW Model</h3>

      {/* Review Progress */}
      {progress && (
        <div style={card}>
          <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Review Status</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
            <StatBox label="Total Fields" value={progress.total} />
            <StatBox label="Reviewed" value={progress.reviewed} color="#2E7D32" />
            <StatBox label="Pending" value={progress.pending} color="#F57F17" />
            <StatBox label="Progress" value={`${progress.progress_pct}%`} color={COLORS.navy} />
          </div>
          <div style={{ height: 8, background: '#E9ECEF', borderRadius: 4, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ height: '100%', borderRadius: 4, width: `${progress.progress_pct}%`,
              background: allReviewed ? COLORS.success : COLORS.gold, transition: 'width 0.3s' }} />
          </div>

          {!deal.template_id ? (
            <TemplateInlineSelector dealId={deal.id} propertyType={deal.property_type} onAssigned={onUpdate} />
          ) : !canPopulate ? (
            <div style={{ padding: 12, background: '#FFF3CD', borderRadius: 4, color: '#856404', fontSize: 13 }}>
              Review and approve extracted fields before populating the model.
            </div>
          ) : (
            <button onClick={handlePopulate} disabled={populating}
              style={{ padding: '12px 28px', background: allReviewed ? COLORS.success : COLORS.gold,
                color: allReviewed ? '#fff' : COLORS.navy, border: 'none', borderRadius: 4,
                fontWeight: 600, cursor: 'pointer', fontSize: 14, opacity: populating ? 0.6 : 1 }}>
              {populating ? 'Populating Model...' : allReviewed ? 'Populate Model (All Reviewed)' : 'Populate Model (Partial)'}
            </button>
          )}
        </div>
      )}

      {/* Generated Models */}
      {models.length > 0 && (
        <div style={card}>
          <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Generated Models</h4>
          {models.map(m => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${COLORS.border}` }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.navy }}>{m.template_name || 'Populated Model'}</div>
                <div style={{ fontSize: 12, color: COLORS.textLight }}>
                  {m.populated_fields_count} fields populated | {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
              <button onClick={() => authDownload(`${API_BASE}/populate/download/${m.id}`, `populated_model_${m.id.slice(0,8)}.xlsx`)}
                style={{ padding: '8px 16px', background: COLORS.navy, color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                Download .xlsx
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: 12, background: '#FAFBFC', borderRadius: 6 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || '#1A2332' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function TemplateInlineSelector({ dealId, propertyType, onAssigned }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/templates').then(res => {
      if (res.success) setTemplates(res.data.filter(t => t.is_active));
      setLoading(false);
    });
  }, []);

  const handleSelect = async (templateId) => {
    const res = await apiFetch(`/deals/${dealId}/template`, { method: 'PUT', body: { template_id: templateId } });
    if (res.success) { onAssigned(); }
    else { alert(res.error || 'Failed to assign template'); }
  };

  if (loading) return <div style={{ padding: 12, color: '#999', fontSize: 13 }}>Loading templates...</div>;

  return (
    <div style={{ padding: 16, background: '#FFF8E1', borderRadius: 6, border: '1px solid #FFE082' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#F57F17', marginBottom: 8 }}>No UW template assigned</div>
      <div style={{ fontSize: 13, color: '#856404', marginBottom: 12 }}>Select a template to enable model population:</div>
      <TemplateSelector templates={templates} selectedId={null} onSelect={handleSelect} propertyType={propertyType} />
    </div>
  );
}
